import React, { useState, useEffect } from 'react';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { devportalApi } from '../../utils/devportalApi';
import PricingCard from './components/PricingCard';
import UsageCalculator from './components/UsageCalculator';
import ComparisonTable from './components/ComparisonTable';
import PricingFAQ from './components/PricingFAQ';
import EnterpriseContact from './components/EnterpriseContact';

const PricingPlans = () => {
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [rawPlans, setRawPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const data = await devportalApi.getPlans();
        setRawPlans(data);
      } catch (err) {
        console.error('Failed to fetch plans:', err);
        setError('Failed to load pricing plans. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchPlans();
  }, []);

  const planMetadata = {
    starter: {
      ctaText: 'Start Free Trial',
      hasDemo: false,
      apiRequests: '10K',
      connections: '100',
      history: '7 days',
      realtime: true,
      attachments: false,
      pushNotifications: false,
      webhooks: true,
      customIntegrations: false,
      analytics: false,
      multiLanguage: false,
      prioritySupport: false,
      sla: 'No',
      branding: false,
      security: false,
      accountManager: false,
      customDeployment: false
    },
    pro: { // Mapping 'business' from API to 'pro' metadata if needed, or just use 'business'
      ctaText: 'Start Free Trial',
      hasDemo: true,
      badge: 'Most Popular',
      apiRequests: '100K',
      connections: '1K',
      history: '30 days',
      realtime: true,
      attachments: true,
      pushNotifications: true,
      webhooks: true,
      customIntegrations: false,
      analytics: true,
      multiLanguage: true,
      prioritySupport: true,
      sla: 'No',
      branding: false,
      security: true,
      accountManager: false,
      customDeployment: false
    },
    business: {
      ctaText: 'Start Free Trial',
      hasDemo: true,
      apiRequests: '500K',
      connections: '5K',
      history: '90 days',
      realtime: true,
      attachments: true,
      pushNotifications: true,
      webhooks: true,
      customIntegrations: true,
      analytics: true,
      multiLanguage: true,
      prioritySupport: true,
      sla: '99.9%',
      branding: true,
      security: true,
      accountManager: false,
      customDeployment: false
    },
    enterprise: {
      ctaText: 'Contact Sales',
      hasDemo: true,
      apiRequests: 'Unlimited',
      connections: 'Unlimited',
      history: 'Custom',
      realtime: true,
      attachments: true,
      pushNotifications: true,
      webhooks: true,
      customIntegrations: true,
      analytics: true,
      multiLanguage: true,
      prioritySupport: true,
      sla: '99.99%',
      branding: true,
      security: true,
      accountManager: true,
      customDeployment: true
    },
    free: {
      ctaText: 'Get Started',
      hasDemo: false,
      apiRequests: '100',
      connections: '5',
      history: '1 day',
      realtime: false,
      attachments: false,
      pushNotifications: false,
      webhooks: false,
      customIntegrations: false,
      analytics: false,
      multiLanguage: false,
      prioritySupport: false,
      sla: 'No',
      branding: false,
      security: false,
      accountManager: false,
      customDeployment: false
    }
  };

  const getPricingPlans = () => {
    if (loading || error || rawPlans.length === 0) return [];

    // Map API plans to UI format
    const mappedPlans = rawPlans.map(plan => {
      const metadata = planMetadata[plan.plan_type] || {};
      const basePrice = plan.price / 100; // Convert cents to dollars

      // Calculate annual price with 20% discount if applicable
      const annualDiscount = billingCycle === 'annual' ? 20 : 0;
      const price = billingCycle === 'annual' && basePrice > 0
        ? basePrice * 0.8
        : basePrice;

      // Parse features
      const featuresList = plan.features ? plan.features.split(',').map(f => f.trim()) : [];

      return {
        id: plan.plan_type,
        name: plan.name,
        price: plan.price === 0 ? 0 : price, // Keep 0 as 0
        description: plan.description,
        annualDiscount: annualDiscount > 0 ? annualDiscount : null,
        features: featuresList,
        isPopular: plan.plan_type === 'business', // Example logic
        ...metadata
      };
    });

    // Sort: Free, Starter, Business, Enterprise
    const order = ['free', 'starter', 'business', 'enterprise'];
    return mappedPlans.sort((a, b) => {
      return order.indexOf(a.id) - order.indexOf(b.id);
    });
  };

  const pricingPlans = getPricingPlans();

  const handlePlanSelection = (plan) => {
    setSelectedPlan(plan);
    if (plan?.id === 'enterprise') {
      // Scroll to enterprise contact form
      document.getElementById('enterprise-contact')?.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    } else {
      // Handle regular plan selection
      console.log('Selected plan:', plan);
      
      // Check if plan has active invoice
      if (plan.hasActiveInvoice) {
        // Navigate directly to payment page
        window.location.href = `/payment?plan_id=${plan.id}`;
      } else {
        // Navigate to developer portal for plan upgrade
        window.location.href = '/developer-portal';
      }
    }
  };

  const handleBillingCycleChange = (cycle) => {
    setBillingCycle(cycle);
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      {/* Hero Section */}
      <section className="pt-24 pb-16 px-6 lg:px-8">
        <div className="max-w-7xl mx-auto text-center">
          <div className="flex items-center justify-center space-x-3 mb-6">
            <div className="flex items-center justify-center w-12 h-12 bg-primary/10 rounded-lg">
              <Icon name="DollarSign" size={24} className="text-primary" />
            </div>
            <h1 className="text-4xl lg:text-5xl font-bold text-foreground">
              Simple, Transparent Pricing
            </h1>
          </div>

          <p className="text-xl text-muted-foreground mb-8 max-w-3xl mx-auto">
            Choose the perfect plan for your messaging needs. Start with our free trial
            and scale as you grow. No hidden fees, no surprises.
          </p>

          {/* Billing Toggle */}
          <div className="flex items-center justify-center space-x-4 mb-12">
            <span className={`text-sm font-medium ${billingCycle === 'monthly' ? 'text-foreground' : 'text-muted-foreground'}`}>
              Monthly
            </span>
            <button
              onClick={() => handleBillingCycleChange(billingCycle === 'monthly' ? 'annual' : 'monthly')}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 ${billingCycle === 'annual' ? 'bg-primary' : 'bg-muted'
                }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-200 ${billingCycle === 'annual' ? 'translate-x-6' : 'translate-x-1'
                  }`}
              />
            </button>
            <span className={`text-sm font-medium ${billingCycle === 'annual' ? 'text-foreground' : 'text-muted-foreground'}`}>
              Annual
            </span>
            {billingCycle === 'annual' && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-accent/10 text-accent">
                <Icon name="Percent" size={12} className="mr-1" />
                Save 20%
              </span>
            )}
          </div>
        </div>
      </section>
      {/* Pricing Cards */}
      <section className="pb-16 px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
          ) : error ? (
            <div className="text-center py-12 text-destructive">
              <p>{error}</p>
              <Button
                variant="outline"
                className="mt-4"
                onClick={() => window.location.reload()}
              >
                Retry
              </Button>
            </div>
          ) : (
            <div className="grid lg:grid-cols-4 gap-8">
              {pricingPlans?.map((plan) => (
                <PricingCard
                  key={plan?.id}
                  plan={plan}
                  isPopular={plan?.isPopular}
                  onSelectPlan={handlePlanSelection}
                />
              ))}
            </div>
          )}
        </div>
      </section>
      {/* Usage Calculator */}
      <section className="py-16 px-6 lg:px-8 bg-muted/20">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-foreground mb-4">
              Calculate Your Costs
            </h2>
            <p className="text-lg text-muted-foreground">
              Use our calculator to estimate your monthly costs based on your expected usage
            </p>
          </div>
          <UsageCalculator />
        </div>
      </section>
      {/* Feature Comparison Table */}
      <section className="py-16 px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-foreground mb-4">
              Compare All Features
            </h2>
            <p className="text-lg text-muted-foreground">
              See exactly what's included in each plan with our detailed comparison
            </p>
          </div>
          <ComparisonTable plans={pricingPlans} onSelectPlan={handlePlanSelection} />
        </div>
      </section>
      {/* Enterprise Contact */}
      <section id="enterprise-contact" className="py-16 px-6 lg:px-8 bg-muted/20">
        <div className="max-w-6xl mx-auto">
          <EnterpriseContact />
        </div>
      </section>
      {/* FAQ Section */}
      <section className="py-16 px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <PricingFAQ />
        </div>
      </section>
      {/* Trust Indicators */}
      <section className="py-16 px-6 lg:px-8 bg-muted/20">
        <div className="max-w-6xl mx-auto text-center">
          <h2 className="text-2xl font-bold text-foreground mb-8">
            Trusted by thousands of developers worldwide
          </h2>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-12">
            {[
              { icon: 'Shield', label: 'SOC 2 Compliant', desc: 'Enterprise security' },
              { icon: 'Clock', label: '99.99% Uptime', desc: 'Reliable service' },
              { icon: 'Users', label: '50K+ Developers', desc: 'Growing community' },
              { icon: 'Globe', label: 'Global CDN', desc: 'Worldwide coverage' }
            ]?.map((item, index) => (
              <div key={index} className="text-center">
                <div className="flex items-center justify-center w-12 h-12 bg-primary/10 rounded-lg mx-auto mb-3">
                  <Icon name={item?.icon} size={24} className="text-primary" />
                </div>
                <h3 className="font-semibold text-foreground mb-1">{item?.label}</h3>
                <p className="text-sm text-muted-foreground">{item?.desc}</p>
              </div>
            ))}
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              variant="default"
              size="lg"
              iconName="ArrowRight"
              iconPosition="right"
              className="shadow-elevation-2"
            >
              Start Free Trial
            </Button>
            <Button
              variant="outline"
              size="lg"
              iconName="MessageSquare"
              iconPosition="left"
            >
              Talk to Sales
            </Button>
          </div>
        </div>
      </section>
      {/* Footer */}
      <footer className="bg-card border-t border-border py-12 px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div className="md:col-span-2">
              <div className="flex items-center space-x-3 mb-4">
                <div className="flex items-center justify-center w-8 h-8 bg-primary rounded-lg">
                  <Icon name="MessageCircle" size={20} color="white" />
                </div>
                <span className="text-xl font-semibold text-foreground">
                  MessagingHub Pro
                </span>
              </div>
              <p className="text-muted-foreground mb-4 max-w-md">
                The most reliable messaging infrastructure for modern applications.
                Build better communication experiences with our powerful APIs.
              </p>
              <div className="flex space-x-4">
                {['Twitter', 'Github', 'Linkedin']?.map((social) => (
                  <button
                    key={social}
                    className="flex items-center justify-center w-10 h-10 bg-muted rounded-lg hover:bg-muted/80 transition-colors duration-200"
                  >
                    <Icon name={social} size={20} className="text-muted-foreground" />
                  </button>
                ))}
              </div>
            </div>

            <div>
              <h3 className="font-semibold text-foreground mb-4">Product</h3>
              <ul className="space-y-2">
                {['API Documentation', 'SDKs', 'Webhooks', 'Status Page']?.map((item) => (
                  <li key={item}>
                    <a href="#" className="text-muted-foreground hover:text-foreground transition-colors duration-200">
                      {item}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h3 className="font-semibold text-foreground mb-4">Company</h3>
              <ul className="space-y-2">
                {['About Us', 'Careers', 'Privacy Policy', 'Terms of Service']?.map((item) => (
                  <li key={item}>
                    <a href="#" className="text-muted-foreground hover:text-foreground transition-colors duration-200">
                      {item}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="border-t border-border pt-8 text-center">
            <p className="text-muted-foreground">
              © {new Date()?.getFullYear()} MessagingHub Pro. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PricingPlans;